// src/components/CommunityBestDisplay.js
import React, { useMemo, useState } from 'react';
import { getMedalThresholds } from '../utils/medalUtils';
import { IoEyeOutline, IoFootstepsOutline, IoShareSocialOutline, IoCheckmarkOutline } from 'react-icons/io5';
import WordRow from './WordRow';
import LengthRow from './LengthRow';

const Spinner = () => (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const ShareButton = ({ currentTier, playerBestLength, onShare }) => {
    const [isCopied, setIsCopied] = useState(false);

    const handleShareClick = () => {
        onShare();
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    if (currentTier === 'default') return null;

    let tierColorClass = 'bg-accent hover:bg-accent-dark dark:bg-dark-accent dark:hover:bg-dark-accent-dark text-background dark:text-dark-background';
    if (currentTier === 'bronze') tierColorClass = 'bg-bronze hover:bg-opacity-90 text-white border border-bronze';
    if (currentTier === 'silver') tierColorClass = 'bg-silver hover:bg-opacity-90 text-gray-800 border border-silver';
    if (currentTier === 'gold') tierColorClass = 'bg-gold hover:bg-opacity-90 text-gray-900 border border-gold';
    if (currentTier === 'platinum') tierColorClass = 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:opacity-90 text-white border-transparent';

    return (
        <button
            onClick={handleShareClick}
            className={`flex items-center justify-center space-x-1 text-sm px-4 py-2 rounded-md transition-all duration-300 w-36 ${tierColorClass}`}
        >
            {isCopied ? (
                <>
                    <IoCheckmarkOutline size={18} />
                    <span>Copied!</span>
                </>
            ) : (
                <>
                    <IoShareSocialOutline size={18} />
                    <span>Share Score</span>
                </>
            )}
        </button>
    );
};

const CommunityBestDisplay = ({ communityBestLength, revealedData, onRevealWord, onRevealPath, isRevealingWord, playerBestLength }) => {
  const showRevealPathButton = revealedData && revealedData.word;

  const milestones = useMemo(() => {
    // Calculate thresholds using the shared utility
    const t = getMedalThresholds(communityBestLength);
    
    const m = {};
    // Assign in increasing order
    m[t.bronze] = 'bronze';
    m[t.silver] = 'silver';
    m[t.gold] = 'gold';
    m[t.platinum] = 'platinum';
    return m;
  }, [communityBestLength]);

  const currentTier = useMemo(() => {
     const t = getMedalThresholds(communityBestLength);
     
     if (playerBestLength >= t.platinum) return 'platinum';
     if (playerBestLength >= t.gold) return 'gold';
     if (playerBestLength >= t.silver) return 'silver';
     if (playerBestLength >= t.bronze) return 'bronze';
     return 'default';
  }, [communityBestLength, playerBestLength]);

  const handleShare = () => {
      const today = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      let tierEmoji = '🧢'; // Default / Playing
      if (currentTier === 'bronze') tierEmoji = '🥉';
      if (currentTier === 'silver') tierEmoji = '🥈';
      if (currentTier === 'gold') tierEmoji = '🥇';
      if (currentTier === 'platinum') tierEmoji = '🏆';
      
      const tierName = currentTier === 'platinum' ? 'Community Best' : currentTier.charAt(0).toUpperCase() + currentTier.slice(1);
      const text = `Wordamid - ${today}\n${tierEmoji} ${tierName} Achieved!\n📏 Best Length: ${playerBestLength}\n\nCan you beat the community?`;
      
      navigator.clipboard.writeText(text).then(() => {
          // Success handled by button state
      }, (err) => {
          console.error('Could not copy text: ', err);
          alert("Failed to copy.");
      });
  };

  return (
    <div className="flex flex-col items-center p-4 my-2 w-full max-w-md mx-auto">
      <h2 className="text-sm font-bold tracking-widest uppercase text-gray-500 dark:text-gray-400">COMMUNITY BEST</h2>
      
      {/* Visual length display or Revealed Word */}
      <div className="flex justify-center items-center my-2 min-h-[40px] sm:min-h-[44px]">
        { revealedData.word
            ? <WordRow word={revealedData.word} size="small"/>
            : communityBestLength > 0 
                ? <LengthRow 
                    length={communityBestLength} 
                    size="small" 
                    progress={playerBestLength}
                    tier={currentTier}
                    milestones={milestones}
                  />
                : <span className="font-bold text-2xl text-gray-500">-</span>
        }
      </div>

      {/* Revealed Path Display */}
      {revealedData.path && (
        <div className="my-2 p-2 text-center text-sm text-gray-600 dark:text-dark-foreground font-mono bg-gray-100 dark:bg-dark-key-bg rounded-md break-words">
            {revealedData.path.join(' → ')}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-4 mt-2 h-8">
        {!revealedData.word && communityBestLength > 0 && (
          <button 
            onClick={onRevealWord} 
            className="flex items-center justify-center space-x-1 text-sm text-background dark:text-dark-background bg-accent hover:bg-accent-dark dark:bg-dark-accent dark:hover:bg-dark-accent-dark px-4 py-2 rounded-md w-36"
            disabled={isRevealingWord}
          >
            {isRevealingWord ? <Spinner /> : (
              <>
                <IoEyeOutline />
                <span>Reveal Word</span>
              </>
            )}
          </button>
        )}
        
        {/* Share Button (Shows up when Tier >= Bronze) */}
        {!revealedData.word && currentTier !== 'default' && (
             <ShareButton 
                currentTier={currentTier} 
                playerBestLength={playerBestLength}
                onShare={handleShare}
             />
        )}

        {showRevealPathButton && !revealedData.path && (
          <button onClick={onRevealPath} className="flex items-center space-x-1 text-sm text-background dark:text-dark-background bg-accent hover:bg-accent-dark dark:bg-dark-accent dark:hover:bg-dark-accent-dark px-4 py-2 rounded-md">
            <IoFootstepsOutline />
            <span>Reveal Path</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default CommunityBestDisplay;
